import { FlagOutlined } from '@ant-design/icons';
import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { closeOverviewURLs } from 'routes/close/closeURLs';
import { colors } from 'theme/colors';

import type { MonthCloseProgress } from 'pages/period-close/demo/types/monthClose';
import { buildCloseProgressSidebarData } from 'pages/period-close/demo/utils/mockMonthData';

import { Button } from 'components/general/Button';
import { OpenLinkOutlined } from 'components/icons';

// Delay before animating progress bars from 0 — ensures the skeleton is painted first
const ANIMATION_DELAY_MS = 150;

// Duration for the progress bar fill animation (ms)
const BAR_ANIMATION_DURATION_MS = 600;

// Simulated loading delay (ms) for the demo skeleton state
const DEMO_LOADING_DELAY_MS = 500;

// TODO: Replace with useGetCloseProgressStatus hook when backend is ready
const DEMO_PROGRESS_DATA: MonthCloseProgress[] = buildCloseProgressSidebarData({
  previousMonthPercent: 100,
  currentMonthPercent: 75,
});

function getBarColor(percent: number, targetDate: string): string {
  if (percent >= 100) return colors.chartColor1;
  const isOverdue = dayjs().isAfter(dayjs(targetDate).endOf('day'));
  return isOverdue ? colors.red4 : colors.cyan7;
}

// ---------------------------------------------------------------------------
// Skeleton — uses subtle lighter-green shimmers on the dark background
// ---------------------------------------------------------------------------
const CloseProgressBoxSkeleton = () => (
  <div
    className="flex flex-col gap-2 rounded-lg p-3"
    style={{ backgroundColor: colors.primaryGreen5 }}
  >
    <div
      className="h-3.5 w-[70%] rounded animate-pulse"
      style={{ backgroundColor: colors.primaryGreen3 }}
    />
    {[1, 2].map((i) => (
      <div key={i} className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <div
            className="h-3 w-[40%] rounded animate-pulse"
            style={{ backgroundColor: colors.primaryGreen3 }}
          />
          <div
            className="h-3 w-[30%] rounded animate-pulse"
            style={{ backgroundColor: colors.primaryGreen3 }}
          />
        </div>
        <div
          className="h-1.5 w-full rounded-lg animate-pulse"
          style={{ backgroundColor: colors.primaryGreen3 }}
        />
      </div>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Progress Bar — animates from 0 → target via requestAnimationFrame
// ---------------------------------------------------------------------------
interface ProgressBarProps {
  percent: number;
  color: string;
  isAnimated: boolean;
}

const ProgressBar = ({ percent, color, isAnimated }: ProgressBarProps) => {
  const [animatedWidth, setAnimatedWidth] = useState(0);

  useEffect(() => {
    if (!isAnimated) return;
    const start = performance.now();
    let raf = requestAnimationFrame(function step(now) {
      const elapsed = Math.min((now - start) / BAR_ANIMATION_DURATION_MS, 1);
      setAnimatedWidth(Math.round(percent * elapsed));
      if (elapsed < 1) raf = requestAnimationFrame(step);
    });
    return () => cancelAnimationFrame(raf);
  }, [isAnimated, percent]);

  return (
    <div className="flex items-center gap-2 w-full">
      <div
        className="flex h-1.5 flex-1 overflow-hidden rounded-lg"
        style={{ backgroundColor: colors.primaryGreen3 }}
      >
        <div
          className="h-full rounded-l-lg"
          style={{
            width: `${isAnimated ? animatedWidth : 0}%`,
            backgroundColor: color,
          }}
        />
      </div>
      <span className="text-xs leading-5 text-white whitespace-nowrap">
        {percent}%
      </span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// CloseProgressBox — compact progress summary for the sidebar
// ---------------------------------------------------------------------------
export const CloseProgressBox = () => {
  const { t } = useTranslation('close-overview');

  const [isExpanded, setIsExpanded] = useState(true);

  // Simulate initial loading for the demo skeleton
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), DEMO_LOADING_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  // Animate progress bars from 0 after loading finishes
  const [isAnimated, setIsAnimated] = useState(false);
  useEffect(() => {
    if (isLoading) return;
    const timer = setTimeout(() => setIsAnimated(true), ANIMATION_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isLoading]);

  if (isLoading) return <CloseProgressBoxSkeleton />;

  return (
    <div
      className="flex flex-col rounded-lg p-3"
      style={{ backgroundColor: colors.primaryGreen5 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium leading-[22px] text-white">
          {t('sidebarProgress.title', { defaultValue: 'Close Progress' })}
        </span>
        <Button
          type="text"
          size="small"
          className="h-4 w-4 min-w-0 border-none bg-transparent! p-0 text-white hover:bg-transparent! hover:text-white!"
          icon={
            <Icon
              icon="mdi:chevron-down"
              className={`h-4 w-4 text-white transition-transform ${isExpanded ? 'rotate-180' : 'rotate-0'}`}
            />
          }
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-label={
            isExpanded
              ? t('sidebarProgress.collapse', {
                  defaultValue: 'Collapse close progress',
                })
              : t('sidebarProgress.expand', {
                  defaultValue: 'Expand close progress',
                })
          }
        />
      </div>

      {/* Collapsible month entries — grid-rows trick for height animation */}
      <div
        className={`grid transition-all duration-300 ease-in-out ${
          isExpanded
            ? 'grid-rows-[1fr] opacity-100'
            : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden flex flex-col gap-2">
          {DEMO_PROGRESS_DATA.map((entry) => {
            const barColor = getBarColor(
              entry.close_progress_percent,
              entry.target_date
            );
            const monthLabel = dayjs(entry.month, 'YYYY-MM').format('MMM YYYY');
            const targetLabel = dayjs(entry.target_date).format('MMM DD');

            return (
              <div key={entry.month} className="flex flex-col">
                <div className="flex items-center justify-between">
                  <Link
                    to={closeOverviewURLs.monthClose}
                    className="inline-flex items-center gap-1 no-underline hover:no-underline"
                    aria-label={t('sidebarProgress.openMonthClose', {
                      defaultValue: 'Open month close page',
                    })}
                  >
                    <span className="text-xs font-medium leading-5 text-white hover:text-white">
                      {monthLabel}
                    </span>
                    <OpenLinkOutlined className="h-3 w-3 text-white opacity-70 hover:text-white" />
                  </Link>
                  <div className="flex items-center gap-1">
                    <FlagOutlined className="h-3 w-3 text-white opacity-70" />
                    <span className="truncate text-xs leading-5 text-white">
                      {targetLabel}
                    </span>
                  </div>
                </div>
                <ProgressBar
                  percent={entry.close_progress_percent}
                  color={barColor}
                  isAnimated={isAnimated}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

CloseProgressBox.displayName = 'CloseProgressBox';
